import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import config from "../../Config";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { toast } from "react-toastify";
import moment from "moment";

/*
 * Additional duties / responsibilities held by one employee - Wing Incharge, Class
 * Teacher, Subject Coordinator, HOD ... - each from a date to a date.
 *
 * Rendered inside the Edit Employee page but self-contained, like StaffQualifications:
 * it loads and saves its own data and its modal carries its own <Form>.
 *
 * Phase 1: the allowance each duty carries is shown here and totalled, but nothing is
 * posted to payroll yet. That is the next phase.
 */

const fmtAmount = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-PK"));
const fmtDate = (d) => (d ? moment(d).format("DD-MMM-YYYY") : "");
const toInputDate = (d) => (d ? moment(d).format("YYYY-MM-DD") : "");

function StaffAdditionalDuties({ staffId, staffName }) {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const UserName = localStorage.getItem("UserName");
  const MemberType = localStorage.getItem("MemberType");

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [duties, setDuties] = useState([]);
  const [currentAllowance, setCurrentAllowance] = useState(0);
  const [showHistory, setShowHistory] = useState(false);

  const blank = {
    ID: 0,
    Duty_ID: "",
    Details: "",
    Allowance_Override: "",
    Effective_From: moment().format("YYYY-MM-DD"),
    Effective_To: "",
    Remarks: "",
  };
  const [showForm, setShowForm] = useState(false);
  const [entry, setEntry] = useState(blank);
  const [validated, setValidated] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showEnd, setShowEnd] = useState(false);
  const [endRow, setEndRow] = useState(null);
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));

  const auth = useCallback(
    (extra) => ({
      AccessKey,
      UserID,
      UserName,
      MemberType: MemberType || "Admin",
      Staff_ID: parseInt(staffId, 10),
      ...extra,
    }),
    [AccessKey, UserID, UserName, MemberType, staffId]
  );

  const post = useCallback(
    (url, data) =>
      axios({
        method: "post",
        url: config.base_url + url,
        headers: { "Content-Type": "application/json" },
        data,
      }),
    []
  );

  const load = useCallback(() => {
    setLoading(true);
    post("AdditionalDuty/GetStaffDuties", auth({}))
      .then((res) => {
        if (res.data.status_code === 1) {
          setRows(res.data.ListofRecords || []);
          setDuties(res.data.Duties || []);
          setCurrentAllowance(res.data.CurrentAllowance || 0);
        } else {
          setRows([]);
          setDuties([]);
          setCurrentAllowance(0);
        }
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [post, auth]);

  useEffect(() => {
    if (staffId) load();
  }, [staffId, load]);

  /* ---- helpers ---- */

  const selectedDuty = duties.find((d) => String(d.ID) === String(entry.Duty_ID)) || null;
  const current = rows.filter((r) => r.Is_Current);
  const past = rows.filter((r) => !r.Is_Current);
  const visible = showHistory ? rows : current;

  const set = (field, value) => setEntry((prev) => ({ ...prev, [field]: value }));

  const dateOrderWrong =
    entry.Effective_To !== "" && entry.Effective_From !== "" && entry.Effective_To < entry.Effective_From;

  /* ---- assign / edit ---- */

  const openAdd = () => {
    setEntry(blank);
    setValidated(false);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEntry({
      ID: a.ID,
      Duty_ID: String(a.Duty_ID),
      Details: a.Details || "",
      Allowance_Override: a.Allowance_Override ?? "",
      Effective_From: toInputDate(a.Effective_From),
      Effective_To: toInputDate(a.Effective_To),
      Remarks: a.Remarks || "",
    });
    setValidated(false);
    setShowForm(true);
  };

  const save = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;
    if (form.checkValidity() === false || dateOrderWrong) {
      setValidated(true);
      return;
    }

    setSaving(true);
    post(
      "AdditionalDuty/SaveStaffDuty",
      auth({
        ID: entry.ID,
        Duty_ID: parseInt(entry.Duty_ID, 10),
        Details: entry.Details,
        Allowance_Override: entry.Allowance_Override === "" ? null : parseFloat(entry.Allowance_Override),
        Effective_From: entry.Effective_From || null,
        Effective_To: entry.Effective_To || null,
        Remarks: entry.Remarks,
      })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          setShowForm(false);
          load();
        } else {
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 8000,
          });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setSaving(false));
  };

  /* ---- end / remove ---- */

  const openEnd = (a) => {
    setEndRow(a);
    setEndDate(moment().format("YYYY-MM-DD"));
    setShowEnd(true);
  };

  const endDuty = () => {
    post("AdditionalDuty/EndStaffDuty", auth({ ID: endRow.ID, Effective_To: endDate || null }))
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          setShowEnd(false);
          load();
        } else {
          toast.error(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }));
  };

  const remove = (a) => {
    if (
      !window.confirm(
        `Remove "${a.Role_Name}"${a.Details ? ` (${a.Details})` : ""} from ${staffName || "this employee"}'s record?\n\n` +
          "Use this only for an assignment made by mistake. For a duty that was actually held, use End instead so the history is kept."
      )
    ) {
      return;
    }
    post("AdditionalDuty/DeleteStaffDuty", auth({ ID: a.ID }))
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          load();
        } else {
          toast.error(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }));
  };

  /* ---- render ---- */

  const statusBadge = (a) => {
    if (a.Is_Current) return <span className="badge bg-success">current</span>;
    if (!a.Is_Active) return <span className="badge bg-secondary">inactive</span>;
    const today = moment().format("YYYY-MM-DD");
    if (toInputDate(a.Effective_From) > today) return <span className="badge bg-info text-dark">upcoming</span>;
    return <span className="badge bg-light text-dark border">ended</span>;
  };

  const renderRows = () => {
    if (visible.length === 0) {
      return (
        <tr>
          <td colSpan="8" className="text-center text-muted py-4">
            {rows.length === 0 ? "No additional responsibilities assigned yet" : "No current responsibilities"}
          </td>
        </tr>
      );
    }

    return visible.map((a, i) => (
      <tr key={a.ID} className={a.Is_Current ? "" : "text-muted"}>
        <td>{i + 1}</td>
        <td className="fw-bold">{a.Role_Name}</td>
        <td>{a.Details || "—"}</td>
        <td className="text-end text-nowrap">
          {fmtAmount(a.Effective_Allowance)}
          {a.Allowance_Override !== null && a.Allowance_Override !== undefined ? (
            <i
              className="ri-information-line ms-1 text-primary"
              title={`Overridden for this employee — the standard amount is ${fmtAmount(a.Duty_Allowance)}`}
            ></i>
          ) : null}
        </td>
        <td className="text-nowrap">{fmtDate(a.Effective_From)}</td>
        <td className="text-nowrap">{a.Effective_To ? fmtDate(a.Effective_To) : <span className="text-muted">open</span>}</td>
        <td>
          {statusBadge(a)}
          {a.Remarks ? <i className="ri-chat-1-line ms-2 text-muted" title={a.Remarks}></i> : null}
        </td>
        <td>
          <ul className="inline-action" aria-labelledby="dropdownMenuButton1">
            <li>
              <button type="button" className="green" title="Edit" onClick={() => openEdit(a)}>
                <i className="ri-edit-line"></i>
              </button>
            </li>
            {a.Is_Current && !a.Effective_To ? (
              <li>
                <button type="button" className="blue" title="End this responsibility" onClick={() => openEnd(a)}>
                  <i className="ri-stop-circle-line"></i>
                </button>
              </li>
            ) : null}
            <li>
              <button type="button" className="red" title="Remove" onClick={() => remove(a)}>
                <i className="ri-delete-bin-line"></i>
              </button>
            </li>
          </ul>
        </td>
      </tr>
    ));
  };

  return (
    <>
      {/* The legend floats 22px above the box; without this margin the card clips it. */}
      <div className="form-wrap mt-4">
        <div className="form-legend" style={{ left: "64px" }}>
          Additional Duties / Responsibilities
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <div className="small">
            {current.length > 0 ? (
              <>
                <b>{current.length}</b> current {current.length === 1 ? "responsibility" : "responsibilities"}
                {" · "}
                <span className="text-muted">Allowance:</span> <b>Rs. {fmtAmount(currentAllowance)}</b>
                <span className="text-muted"> / month</span>
                {past.length > 0 ? (
                  <>
                    {" · "}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => setShowHistory((v) => !v)}
                    >
                      {showHistory ? "hide" : "show"} {past.length} past
                    </button>
                  </>
                ) : null}
              </>
            ) : (
              <span className="text-muted">
                Wing Incharge, Class Teacher, HOD … an employee may hold more than one.
                {past.length > 0 ? (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => setShowHistory((v) => !v)}
                    >
                      {showHistory ? "hide" : "show"} {past.length} past
                    </button>
                  </>
                ) : null}
              </span>
            )}
          </div>
          <button type="button" className="button button-primary button-small" onClick={openAdd}>
            <i className="ri-add-line me-1"></i>Assign Responsibility
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-theme">
            <thead>
              <tr>
                <th>Sr.</th>
                <th>Responsibility</th>
                <th>Details</th>
                <th className="text-end">Allowance</th>
                <th>From</th>
                <th>To</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-3">
                    <Spinner animation="border" size="sm" role="status" />
                  </td>
                </tr>
              ) : (
                renderRows()
              )}
            </tbody>
          </table>
        </div>
        <div className="form-text">
          Allowances are recorded here now and will be applied in payroll automatically in the next phase.
        </div>
      </div>

      {/* assign / edit */}
      <Modal className="modal-theme" show={showForm} onHide={() => setShowForm(false)} size="lg" centered>
        <Form noValidate validated={validated} onSubmit={save}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "18px" }}>
              {entry.ID ? "Edit responsibility" : "Assign responsibility"}
              {staffName ? <span className="text-muted fw-normal"> — {staffName}</span> : null}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">
                  Responsibility <span className="text-danger">*</span>
                </label>
                <Form.Select required value={entry.Duty_ID} onChange={(e) => set("Duty_ID", e.target.value)}>
                  <option value="">-- select --</option>
                  {duties.map((d) => (
                    <option key={d.ID} value={d.ID}>
                      {d.Role_Name}
                      {d.Allowance_Value > 0 ? ` (Rs. ${fmtAmount(d.Allowance_Value)})` : ""}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">Choose the responsibility.</Form.Control.Feedback>
                {selectedDuty && selectedDuty.Description ? (
                  <div className="form-text">{selectedDuty.Description}</div>
                ) : null}
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  Details {selectedDuty && selectedDuty.Requires_Details ? <span className="text-danger">*</span> : null}
                </label>
                <Form.Control
                  type="text"
                  required={!!(selectedDuty && selectedDuty.Requires_Details)}
                  maxLength={250}
                  placeholder={
                    selectedDuty && selectedDuty.Requires_Details
                      ? "Describe the duty"
                      : "Class 5-B, Chemistry, Science Dept …"
                  }
                  value={entry.Details}
                  onChange={(e) => set("Details", e.target.value)}
                />
                <Form.Control.Feedback type="invalid">Describe the duty.</Form.Control.Feedback>
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Effective from <span className="text-danger">*</span>
                </label>
                <Form.Control
                  type="date"
                  required
                  value={entry.Effective_From}
                  onChange={(e) => set("Effective_From", e.target.value)}
                />
                <Form.Control.Feedback type="invalid">Enter the start date.</Form.Control.Feedback>
              </div>
              <div className="col-md-4">
                <label className="form-label">Effective to</label>
                <Form.Control
                  type="date"
                  isInvalid={dateOrderWrong}
                  value={entry.Effective_To}
                  onChange={(e) => set("Effective_To", e.target.value)}
                />
                <Form.Control.Feedback type="invalid">The end date is before the start date.</Form.Control.Feedback>
                <div className="form-text">Leave blank if open-ended.</div>
              </div>
              <div className="col-md-4">
                <label className="form-label">Allowance override</label>
                <Form.Control
                  type="number"
                  min={0}
                  step="1"
                  placeholder={selectedDuty ? `standard: ${fmtAmount(selectedDuty.Allowance_Value)}` : "standard amount"}
                  value={entry.Allowance_Override}
                  onChange={(e) => set("Allowance_Override", e.target.value)}
                />
                <div className="form-text">Leave blank to use the standard amount.</div>
              </div>

              <div className="col-md-12">
                <label className="form-label">Remarks</label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={500}
                  value={entry.Remarks}
                  onChange={(e) => set("Remarks", e.target.value)}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="button button-light" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button type="submit" className="button button-primary" disabled={saving}>
              {saving ? "Saving…" : entry.ID ? "Update" : "Assign"}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* end */}
      <Modal className="modal-theme" show={showEnd} onHide={() => setShowEnd(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "18px" }}>End responsibility</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {endRow ? (
            <>
              <p className="mb-2">
                <b>{endRow.Role_Name}</b>
                {endRow.Details ? ` (${endRow.Details})` : ""} — held since {fmtDate(endRow.Effective_From)}
              </p>
              <label className="form-label">Last day</label>
              <Form.Control
                type="date"
                min={toInputDate(endRow.Effective_From)}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <div className="form-text">The record is kept as history; the allowance stops after this date.</div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="button button-light" onClick={() => setShowEnd(false)}>
            Cancel
          </button>
          <button type="button" className="button button-primary" onClick={endDuty}>
            End Responsibility
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default StaffAdditionalDuties;
